export function resolveColorValue(value, context) {
    if (value.type === 'literal')
        return value.hex;
    return context.tokens.colors[value.key] ?? value.key;
}
function gradientStopsCss(stops, context) {
    return stops
        .map((stop) => `${resolveColorValue(stop.color, context)} ${Math.round(stop.offset * 100)}%`)
        .join(', ');
}
/** Resolves a Fill to CSS background properties. */
export function fillToCss(fill, context) {
    if (!fill)
        return {};
    switch (fill.kind) {
        case 'solid':
            return { backgroundColor: resolveColorValue(fill.color, context) };
        case 'linear': {
            const stops = gradientStopsCss(fill.stops, context);
            return {
                backgroundColor: resolveColorValue(fill.stops[0]?.color ?? { type: 'literal', hex: '#000' }, context),
                backgroundImage: `linear-gradient(${fill.angle}deg, ${stops})`,
            };
        }
        case 'radial': {
            const stops = gradientStopsCss(fill.stops, context);
            const cx = Math.round(fill.center.x * 100);
            const cy = Math.round(fill.center.y * 100);
            const r = Math.round(fill.radius * 100);
            return {
                backgroundColor: resolveColorValue(fill.stops[0]?.color ?? { type: 'literal', hex: '#000' }, context),
                backgroundImage: `radial-gradient(circle ${r}% at ${cx}% ${cy}%, ${stops})`,
            };
        }
        case 'image': {
            const url = context.assets.get(fill.assetId);
            // Keep the page surface under photo fills so a missing/broken CDN asset
            // never flashes as a bare white leaf under light text.
            const surfaceFallback = {
                backgroundColor: context.tokens.colors.surface ?? '#0a0a0a',
            };
            if (!url) {
                context.diagnostics.push({
                    level: 'warning',
                    code: 'image-missing',
                    message: `No image for fill asset "${fill.assetId}"`,
                });
                return surfaceFallback;
            }
            const size = fill.fit === 'contain'
                ? 'contain'
                : fill.fit === 'fill'
                    ? '100% 100%'
                    : 'cover';
            return {
                ...surfaceFallback,
                backgroundImage: `url("${url}")`,
                backgroundSize: size,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            };
        }
        default:
            return {};
    }
}
/** Resolves legacy ColorRef or Fill for shape nodes. */
export function paintToCss(paint, context) {
    if (!paint)
        return {};
    if (typeof paint === 'string') {
        return { backgroundColor: context.tokens.colors[paint] ?? paint };
    }
    return fillToCss(paint, context);
}
/** Resolves effect stack to box-shadow and filter. */
export function effectsToCss(effects, context) {
    if (!effects?.length)
        return {};
    const shadows = [];
    const filters = [];
    for (const effect of effects) {
        if (effect.kind === 'blur') {
            filters.push(`blur(${effect.radius * (context.tokens.duScale ?? 1)}px)`);
            continue;
        }
        const color = withAlpha(resolveColorValue(effect.color, context), effect.opacity);
        const inset = effect.kind === 'innerShadow' ? 'inset ' : '';
        const scale = context.tokens.duScale ?? 1;
        shadows.push(`${inset}${effect.offsetX * scale}px ${effect.offsetY * scale}px ${effect.blur * scale}px ${effect.spread * scale}px ${color}`);
    }
    return {
        boxShadow: shadows.length > 0 ? shadows.join(', ') : undefined,
        filter: filters.length > 0 ? filters.join(' ') : undefined,
    };
}
function withAlpha(hex, opacity) {
    const alpha = Math.round(Math.max(0, Math.min(1, opacity)) * 255)
        .toString(16)
        .padStart(2, '0');
    return /^#[0-9a-fA-F]{6}$/.test(hex) ? `${hex}${alpha}` : hex;
}
//# sourceMappingURL=resolve-fill.js.map